import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled, { css } from 'styled-components';
import { Row, Button, Separator, Text, Col } from '@cuemath/comps';
import { Header } from 'semantic-ui-react';
import { closeModal } from 'actions/modal';
import COLORS from 'constants/colors';
import {
  FORM_DATA,
  SUCCESSFULLY_SUBMITTED,
  PLEASE_SELECT_FILE,
  NOTE_FOR_ERROR_COLUMN,
  POST,
  CONTENT_TYPE,
  CSV_CONTENT_TYPE,
  CONTENT_DISPOSITION,
  CSV_LINK,
} from 'constants/pricing-management';
import {
  UPLOAD_FILE,
  STUDENT_PAYMENTS_HEADINGS,
  STUDENT_PAYMENTS_URL,
  CSV_LINK_FOR_UPLOAD_PAYMENTS,
  CSV_LINK_FOR_UPDATE_PAYMENTS,
  ERROR_MSG,
} from 'constants/student-payments';
import PropTypes from 'prop-types';
import { CSVLink } from 'react-csv';
import { ButtonWrapper, Field, Head, Message } from 'modules/form';
import { CSVFormatApi } from 'modals/utils';

const Wrapper = styled.div`
  max-height: 500px;
`;

const AnchorLink = styled.a`
  text-align: left;
  text-decoration: underline;
  color: ${COLORS.BOSTON_BLUE};
  &:hover {
    color: ${COLORS.BOSTON_BLUE};
  }
`;

const FormWrapper = styled.div`
  padding: 20px;
  text-align: center;
`;

const TextWrapper = styled(Text)`
  font-size: 16px;
  ${({ paddingLeft }) =>
    paddingLeft &&
    css`
      padding-left: ${paddingLeft}px;
    `};
`;

const HeadWrapper = styled(Head)`
  border-bottom: 1px solid ${COLORS.DARK_CHARCOAL};
`;

const CSVLinkWrapper = styled(CSVLink)`
  display: none;
`;

class StudentPaymentModal extends PureComponent {
  static propTypes = {
    actions: PropTypes.shape({
      closeModal: PropTypes.func.isRequired,
    }).isRequired,
    heading: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      errorText: null,
      file: null,
      message: null,
      studentPaymentsData: '',
      noteMsg: null,
      formData: null,
      filename: null,
    };

    this.csvLink = React.createRef();
    this.closeModalTimer = null;
  }

  componentWillUnmount() {
    if (this.closeModalTimer) {
      clearTimeout(this.closeModalTimer);
      this.closeModalTimer = null;
    }
  }

  bakeFormData = isUpdateExistingPayments => {
    const { file } = this.state;

    const { ACTION, FILE, MODIFY, UPLOAD } = FORM_DATA || {};
    this.setState({
      errorText: null,
    });

    const formData = new FormData();
    formData.append(FILE, file);

    formData.append(ACTION, isUpdateExistingPayments ? MODIFY : UPLOAD);

    return formData;
  };

  onChange = event => {
    const { target: { files } = {} } = event || {};

    const file = Array.from(files) && files.length ? files[0] : null;

    this.setState({
      file,
      errorText: null,
    });
  };

  renderUsage = isHeadingModifyPayments => {
    const usagePoints = [
      `Modify/overwite all the fields given csv`,
      'Change validity, classes purchased, classes balance, class ratio of student course',
    ];

    if (isHeadingModifyPayments) {
      return (
        <>
          <Row>
            <TextWrapper display="inline">
              <li>
                Use this facility to{' '}
                <TextWrapper display="inline" weight="semibold">
                  modify or edit student payment links
                </TextWrapper>
              </li>
            </TextWrapper>
          </Row>
          <Separator transparent height={25} />
          <TextWrapper>
            <li>You can use this to:</li>
          </TextWrapper>
          {usagePoints.map(value => (
            <TextWrapper key={value} paddingLeft={20}>
              <li>{value}</li>
            </TextWrapper>
          ))}
        </>
      );
    }

    return (
      <Row>
        <TextWrapper display="inline">
          <li>
            Use this facility only to{' '}
            <TextWrapper display="inline" weight="semibold">
              upload{' '}
            </TextWrapper>
            plans{' '}
            <Text display="inline">
              (If you wish to modify or overwrite existing payments, use the 'Modify
              Student Payments Links' option)
            </Text>
          </li>
        </TextWrapper>
      </Row>
    );
  };

  linksWrapper = isUpdateExistingPlans => {
    const csvFormatLink = isUpdateExistingPlans
      ? CSV_LINK_FOR_UPDATE_PAYMENTS
      : CSV_LINK_FOR_UPLOAD_PAYMENTS;

    return (
      <Row>
        <Col xs={12} gutter={0} gap={0}>
          <AnchorLink href={csvFormatLink} target="_blank">
            <li>CSV Format to upload</li>
          </AnchorLink>
        </Col>
      </Row>
    );
  };

  uploadFiles = isModifyStudentPayments => {
    const { UPLOAD_MODIFIED_PAYMENTS_HERE, UPLOAD_NEW_PAYMENTS_HERE } = UPLOAD_FILE || {};

    const heading = isModifyStudentPayments
      ? UPLOAD_MODIFIED_PAYMENTS_HERE
      : UPLOAD_NEW_PAYMENTS_HERE;

    return (
      <Row>
        <Col xs={4} gutter={0} gap={0}>
          {' '}
          <Text display="inline">
            <li>{heading}</li>
          </Text>
        </Col>
        <Col xs={8} gutter={0} gap={0}>
          <Field key="fileUpload">
            <input
              name="fileUpload"
              id="inputFile"
              placeholder="Upload File"
              type="file"
              onChange={this.onChange}
              onClick={event => {
                event.target.value = null;
              }}
              required={true}
              accept=".csv,text/csv"
            />
          </Field>
        </Col>{' '}
      </Row>
    );
  };

  fetchFilenameFromHeaders = headers => {
    if (headers && headers[CONTENT_DISPOSITION]) {
      const splittedFilename = headers[CONTENT_DISPOSITION].split('=');
      if (Array.isArray(splittedFilename) && splittedFilename.length === 2) {
        this.setState({ filename: splittedFilename[1] });
      }
    }
  };

  onSubmit = async (event, isUpdateExistingPlans) => {
    const { actions } = this.props;
    const { file } = this.state;

    event.preventDefault();

    const formData = this.bakeFormData(isUpdateExistingPlans);

    if (!file) {
      this.setState({ errorText: PLEASE_SELECT_FILE });
      return;
    }

    this.setState({ studentPaymentsData: '', errorText: null, noteMsg: null });

    const studentPaymentData = await CSVFormatApi(POST, STUDENT_PAYMENTS_URL, formData);

    const { response, error } = studentPaymentData || {};

    if (response) {
      const { data, headers } = response || {};
      this.setState({ filename: null });
      this.fetchFilenameFromHeaders(headers);

      this.setState({ errorText: null });

      if (data && headers && headers[CONTENT_TYPE].includes(CSV_CONTENT_TYPE)) {
        this.setState({
          errorText: ERROR_MSG,
          studentPaymentsData: data,
          noteMsg: NOTE_FOR_ERROR_COLUMN,
        });

        const ele = document.getElementById(CSV_LINK);
        if (ele) {
          ele.click();
        }
      } else {
        this.setState({ errorText: null, message: SUCCESSFULLY_SUBMITTED });
        this.closeModalTimer = setTimeout(() => {
          actions.closeModal();
        }, 3000);
      }
    }

    if (error) {
      this.setState({ errorText: error });
    }
  };

  renderButton = isUpdateExistingPlans => {
    const { studentPaymentsData, filename } = this.state;

    return (
      <Header floated="left">
        <ButtonWrapper flexDirection="row-reverse">
          <CSVLinkWrapper
            data={studentPaymentsData}
            filename={filename}
            uFEFF={false}
            id={CSV_LINK}
          >
            Submit
          </CSVLinkWrapper>

          <Button label="Submit" onClick={e => this.onSubmit(e, isUpdateExistingPlans)} />
        </ButtonWrapper>
      </Header>
    );
  };

  render() {
    const { errorText, message, noteMsg } = this.state;
    const { heading } = this.props;

    const { MODIFY_PAYMENTS } = STUDENT_PAYMENTS_HEADINGS || {};

    const isModifyStudentPayments = heading === MODIFY_PAYMENTS;

    return (
      <Wrapper>
        <HeadWrapper>
          <Text size="xlarge" align="center">
            {heading}
          </Text>
        </HeadWrapper>
        <Separator transparent height={25} />
        <FormWrapper width="100%">
          {this.renderUsage(isModifyStudentPayments)}
          <Separator transparent height={25} />
          {this.linksWrapper(isModifyStudentPayments)}
          <Separator transparent height={25} />
          {this.uploadFiles(isModifyStudentPayments)}
          {errorText && (
            <Message width="100%" error={errorText === ERROR_MSG ? false : true}>
              {errorText}
              {noteMsg && <Row justifyContent="center">{noteMsg}</Row>}
            </Message>
          )}
          {message && <Message width="100%">{message}</Message>}
          <Separator transparent height={25} />
          {this.renderButton(isModifyStudentPayments)}
        </FormWrapper>
      </Wrapper>
    );
  }
}

export default connect(null, dispatch => ({
  actions: bindActionCreators(
    {
      closeModal,
    },
    dispatch,
  ),
}))(StudentPaymentModal);
